// @ts-ignore
import React from 'react';
// @ts-ignore
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  position: relative;
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  padding: 20px;
  
  @media (max-width: 480px) {
    padding: 15px;
    justify-content: flex-start;
    padding-top: 80px;
  }
`;

const LoginCard = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(76, 110, 245, 0.1);
  padding: 2rem 2.5rem;
  width: 100%;
  max-width: 480px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: transform 0.3s ease;
  animation: ${fadeIn} 0.5s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(76, 110, 245, 0.15);
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;
    &:hover { transform: none; }
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.8rem;
`;

const LogoImage = styled.img`
  max-width: 339px;
  max-height: 75px;
  margin-bottom: 10px;
  object-fit: contain;
  @media (max-width: 480px) {
    max-width: 260px;
    max-height: 55px;
  }
`;

const LogoText = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
    border-radius: 2px;
    margin: 10px auto 0;
  }
`;

const Description = styled.p`
  text-align: center;
  color: #718096;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: #f8fafc;
  &:focus {
    outline: none;
    border-color: #4c6ef5;
    background: white;
    box-shadow: 0 0 0 4px rgba(76, 110, 245, 0.15);
  }
  &::placeholder { color: #cbd5e0; }
`;

const Button = styled.button`
  display: block;
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 110, 245, 0.2);
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 110, 245, 0.3);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(76, 110, 245, 0.2);
  }
  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
  @media (max-width: 480px) { padding: 16px 20px; }
`;

const HomeButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 110, 245, 0.2);
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  @media (max-width: 480px) {
    padding: 10px 14px;
    font-size: 0.85rem;
    top: 15px;
    left: 15px;
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 110, 245, 0.3);
  }
  &:active {
    transform: translateY(0);
  }
`;

const Message = styled.div<{ type: 'error' | 'success' | 'info' }>`
  font-size: 0.95rem;
  margin-top: 1rem;
  padding: 12px 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${fadeIn} 0.3s ease;
  ${props => props.type === 'error' && `
    color: #c53030;
    background-color: #fff5f5;
    border: 1px solid #feb2b2;
  `}
  ${props => props.type === 'success' && `
    color: #276749;
    background-color: #f0fff4;
    border: 1px solid #9ae6b4;
  `}
  ${props => props.type === 'info' && `
    color: #2b6cb0;
    background-color: #ebf8ff;
    border: 1px solid #90cdf4;
  `}
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 1rem 0;
`;

const OTPHorizontalLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const OTPLeftSection = styled.div`
  flex-shrink: 0;
`;

const OTPRightSection = styled.div`
  flex: 1;
  text-align: center;
`;

const OTPInput = styled.input`
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #2d3748;
  transition: all 0.2s ease;
  &:hover { border-color: #cbd5e0; }
  &:focus {
    outline: none;
    border-color: #4c6ef5;
    box-shadow: 0 0 0 4px rgba(76, 110, 245, 0.15);
    background: white;
  }
  &::placeholder { color: #cbd5e0; }
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: #4c6ef5;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 1rem;
  &:hover { color: #3b5bdb; }
  &:disabled {
    color: #a0aec0;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const Spinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid white;
  border-radius: 50%;
  border-top-color: transparent;
  animation: ${spin} 0.8s linear infinite;
`;

const EmailIcon = styled.div<{ $horizontal?: boolean }>`
  width: ${props => props.$horizontal ? '70px' : '80px'};
  height: ${props => props.$horizontal ? '70px' : '80px'};
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${props => props.$horizontal ? '0' : '0 auto 1.5rem'};
  animation: ${pulse} 2s ease infinite;
  flex-shrink: 0;
  svg {
    width: ${props => props.$horizontal ? '35px' : '40px'};
    height: ${props => props.$horizontal ? '35px' : '40px'};
    color: white;
  }
`;

const SubAdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success' | 'info'; text: string } | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Server-side check if already logged in as sub-admin (cookie-based, not localStorage)
    const verifyExistingSession = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/auth/check`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated && data.isSubAdmin) {
            navigate('/admin/dashboard');
            return;
          }
        }
      } catch {
        // Ignore errors — just show login page
      }
      // Cookie expired or invalid — clear stale localStorage
      localStorage.removeItem('subAdminAuth');
      localStorage.removeItem('subAdminInfo');
    };
    verifyExistingSession();
  }, [navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (cooldown === 0 && message?.type === 'info' && message?.text?.includes('seconds before requesting')) {
      setMessage(null);
    }
  }, [cooldown, message]);

  useEffect(() => {
    if (cooldown > 0 && message?.type === 'info' && message?.text?.includes('seconds before requesting')) {
      setMessage({ type: 'info', text: `Please wait ${cooldown} seconds before requesting another OTP.` });
    }
  }, [cooldown]);

  // Auto-verify when all 6 digits are entered
  useEffect(() => {
    const otpValue = otp.join('');
    if (otpValue.length === 6 && step === 'otp') {
      handleVerifyOTP(otpValue);
    }
  }, [otp, step]);

  const handleSendOTP = async () => {
    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/su-admin/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setStep('otp');
        setMessage({ type: 'success', text: 'OTP sent to your email. Please check your inbox.' });
        setCooldown(60);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        const rateLimitMatch = data.message?.match(/Please wait (\d+) seconds/);
        if (rateLimitMatch) {
          const remainingSeconds = parseInt(rateLimitMatch[1], 10);
          setCooldown(remainingSeconds);
          setMessage({ type: 'info', text: `Please wait ${remainingSeconds} seconds before requesting another OTP.` });
        } else {
          setMessage({ type: 'error', text: data.message || 'Failed to send OTP.' });
        }
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to send OTP. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otpValue: string) => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/su-admin/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, otp: otpValue })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
        localStorage.setItem('subAdminAuth', 'true');
        localStorage.setItem('subAdminInfo', JSON.stringify(data.subAdmin));
        localStorage.removeItem('adminAuth');
        setTimeout(() => navigate('/admin/dashboard'), 500);
      } else {
        setMessage({ type: 'error', text: data.message || 'Invalid OTP.' });
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'OTP verification failed. Please try again.' });
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      otpRefs.current[5]?.focus();
    }
  };

  return (
    <PageContainer>
      <HomeButton onClick={() => navigate('/')}>
        <span>&#8592;</span> Home
      </HomeButton>
      <LoginCard>
        <LogoContainer>
          <LogoImage src="/assets/logo.png" alt="CNG Dealership Logo" />
          <LogoText>Sub-Admin Portal</LogoText>
        </LogoContainer>

        {step === 'email' && (
          <>
            <Description>
              Enter your registered sub-admin email address to receive a One-Time Password (OTP).
            </Description>
            <div style={{ marginBottom: '1rem' }}>
              <InputLabel>Email Address</InputLabel>
              <Input
                type="email"
                placeholder="Enter your sub-admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendOTP(); } }}
                autoComplete="email"
                autoFocus
              />
            </div>
            <Button onClick={handleSendOTP} disabled={isLoading || !email}>
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <Spinner /> Sending OTP...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Send OTP
                </span>
              )}
            </Button>
          </>
        )}

        {step === 'otp' && (
          <OTPHorizontalLayout>
            <OTPLeftSection>
              <EmailIcon $horizontal>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </EmailIcon>
            </OTPLeftSection>
            <OTPRightSection>
              <Description style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                Enter the 6-digit OTP sent to <strong>{email}</strong>
              </Description>

              <OTPContainer onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <OTPInput
                    key={index}
                    ref={el => otpRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    disabled={isLoading}
                    placeholder="•"
                    autoFocus={index === 0}
                    style={{ width: '42px', height: '50px', fontSize: '20px' }}
                  />
                ))}
              </OTPContainer>

              {isLoading && (
                <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                  <Spinner style={{ borderColor: '#4c6ef5', borderTopColor: 'transparent' }} />
                  <p style={{ color: '#718096', marginTop: '0.5rem', fontSize: '0.85rem' }}>Verifying...</p>
                </div>
              )}

              <ResendLink
                onClick={handleSendOTP}
                disabled={cooldown > 0 || isLoading}
                style={{ marginTop: '0.5rem' }}
              >
                {cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}
              </ResendLink>

              <div style={{ marginTop: '0.75rem' }}>
                <ResendLink
                  onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); setMessage(null); }}
                  disabled={isLoading}
                  style={{ color: '#718096', textDecoration: 'none', fontSize: '0.85rem' }}
                >
                  ← Change email
                </ResendLink>
              </div>
            </OTPRightSection>
          </OTPHorizontalLayout>
        )}

        {message && (
          <Message type={message.type}>
            {message.type === 'error' && '\u26A0\uFE0F'}
            {message.type === 'success' && '\u2713'}
            {message.type === 'info' && '\u2139\uFE0F'}
            {message.text}
          </Message>
        )}
      </LoginCard>
    </PageContainer>
  );
};

export default SubAdminLogin;
